import type { CSSProperties, ReactNode } from 'react'

import { Bezel } from '../bezel'
import { PROJECTS } from '../data'
import { useDesk95Theme, useIsDark } from '../theme'

const typeface: CSSProperties = {
    fontFamily: "'Lucida Console', 'Courier New', monospace"
}

const COUNTER = '0014972'

const MARQUEE = '~*~ WeLcOmE 2 MY HoMePaGe ~*~ thanx 4 stopping by ~*~ sign the guestbook b4 u go!! ~*~'

const BASICS: [string, string][] = [
    ['Name:', 'Margaux (but my friends call me Mo)'],
    ['Age:', 'old enough 2 know better ;)'],
    ['Job:', 'art director + photographer'],
    ['Lives:', 'Paris, sixth floor, no lift'],
    ['Loves:', 'contact sheets, flat light, cold coffee']
]

export const Notepad = (): ReactNode => {
    const palette = useDesk95Theme()
    const isDark = useIsDark()

    const heading = isDark ? '#8a8aff' : '#0000cc'
    const stripes = isDark
        ? 'repeating-linear-gradient(45deg, #1a1a1a 0 6px, #212121 6px 12px)'
        : 'repeating-linear-gradient(45deg, #ece2c9 0 6px, #f7f1e3 6px 12px)'

    const bar: CSSProperties = {
        background: palette.titleActive,
        color: palette.titleActiveText
    }

    return (
        <div className='size-full p-[3px]' style={{ background: palette.face }}>
            <Bezel
                variant='sunken'
                face={false}
                className='desk95-scroll size-full overflow-y-auto'
                style={{ ...typeface, background: stripes, color: palette.pageText }}
            >
                <div
                    className='mx-auto flex w-full max-w-[420px] flex-col gap-[12px] px-[12px] py-[10px]'
                    style={{ background: palette.page }}
                >
                    <div className='overflow-hidden whitespace-nowrap px-[3px] py-[2px] text-[11px] leading-[15px]' style={bar}>
                        <span className='inline-block' style={{ animation: 'desk95-marquee 18s linear infinite' }}>
                            <span className='pr-[40px]'>{MARQUEE}</span>
                            <span className='pr-[40px]'>{MARQUEE}</span>
                        </span>
                    </div>

                    <h2 className='text-center text-[15px] leading-[1.3] font-bold' style={{ color: heading }}>
                        Margaux&apos;s Lil Corner of the Web
                    </h2>

                    <div className='flex flex-col items-center gap-[6px]'>
                        <div
                            className='bg-white p-[4px]'
                            style={{ border: '3px solid #000000', boxShadow: `5px 5px 0 0 ${palette.shadow}` }}
                        >
                            <img
                                src='/desk-95/boot-figure.png'
                                alt='Portrait of the studio founder, halftoned to black and white'
                                className='block h-[132px] w-[164px] object-cover object-top'
                                style={{ filter: 'grayscale(1) contrast(1.5)', background: '#ffffff' }}
                                draggable={false}
                            />
                        </div>

                        <p className='text-center text-[11px]'>&hearts; thatz me!! ^_^ &hearts;</p>
                    </div>

                    <p className='text-[12px] leading-[1.55]'>
                        Hiya!! *waves* This is my little corner of the World Wide Web. Grab a soda, kick off
                        your shoes, and stay a while!!
                    </p>

                    <div className='flex flex-wrap items-center justify-center gap-[8px] text-[11px]'>
                        <span>you are visitor no.</span>

                        <span
                            className='flex gap-[2px] px-[4px] py-[3px]'
                            style={{ background: palette.ledFace, boxShadow: 'inset 1px 1px 0 0 #000000' }}
                        >
                            {COUNTER.split('').map((digit, index) => (
                                <span
                                    key={index}
                                    className='px-[2px] text-[12px] leading-none'
                                    style={{ ...typeface, color: palette.led }}
                                >
                                    {digit}
                                </span>
                            ))}
                        </span>
                    </div>

                    <div className='px-[4px] py-[2px] text-[11px] leading-[15px] font-bold' style={bar}>
                        &#9654; THE BASICS
                    </div>

                    <dl className='flex flex-col gap-[4px] text-[12px] leading-[1.45]'>
                        {BASICS.map(([label, value]) => (
                            <div key={label} className='flex flex-wrap gap-[8px]'>
                                <dt className='w-[52px] shrink-0 font-bold' style={{ color: heading }}>{label}</dt>
                                <dd className='min-w-0 flex-1'>{value}</dd>
                            </div>
                        ))}
                    </dl>

                    <div className='px-[4px] py-[2px] text-[11px] leading-[15px] font-bold' style={bar}>
                        &#9654; WHAT I DO
                    </div>

                    <p className='text-[12px] leading-[1.55]'>
                        I started out retouching contact sheets by hand and never quite stopped thinking in
                        frames of ten. Everything on this desktop is a working file, not a case study: open a{' '}
                        <b>.PSD</b>, drag it somewhere better, close it when you have seen enough. There are{' '}
                        {PROJECTS.length} of them, and they run from 1995 to 1999.
                    </p>

                    <ul className='flex flex-col gap-[4px] text-[12px] leading-[1.5]'>
                        <li>&raquo; Art direction, campaign and editorial</li>
                        <li>&raquo; Still life and product photography</li>
                        <li>&raquo; Print, books and identity systems</li>
                    </ul>

                    <hr style={{ borderTop: `1px dashed ${palette.pageText}`, opacity: 0.4 }} />

                    <p className='text-center text-[11px] opacity-70'>
                        Best viewed at 800 &times; 600. Last updated 14 August 1999.
                    </p>
                </div>
            </Bezel>
        </div>
    )
}
